import { useEffect, useMemo, useState } from 'react';
import { FormsMediaManager } from '@frontend/api-forms';
import { useQuery } from '@frontend/react-query-helpers';
import { useDynamicFormStore } from '../store';
import { base64ToBytes, isPdfBytes, sniffImageType } from './signature-image';

export const mediaBlobQueryKey = (mediaId: string) => ['digital-forms', 'media-blob', mediaId] as const;

const downloadMediaBlob = async (mediaId: string): Promise<Blob> => {
  const content = await FormsMediaManager.API.downloadMedia(mediaId);
  const bytes = base64ToBytes(content);
  if (!bytes.length) throw new Error('Empty media content');
  const blob = new Blob([bytes]);
  const type = isPdfBytes(bytes) ? 'application/pdf' : (await sniffImageType(blob)) ?? 'application/octet-stream';
  return new Blob([bytes], { type });
};

interface UseMediaBlobOptions {
  enabled?: boolean;
  onError?: (error: unknown) => void;
}

/**
 * Resolves a committed media id to its bytes. Prefers the local file of an attachment already in the store
 * (just uploaded or restored from a draft) so a fresh signature never round-trips the network.
 */
export const useMediaBlob = (mediaId: string, { enabled = true, onError }: UseMediaBlobOptions = {}) => {
  const { attachments } = useDynamicFormStore(['attachments']);
  const localAttachment = useMemo(
    () =>
      mediaId
        ? Object.values(attachments).find(
            (attachment) => attachment.mediaId === mediaId && attachment.status === 'success'
          )
        : undefined,
    [attachments, mediaId]
  );

  const query = useQuery({
    queryKey: mediaBlobQueryKey(mediaId),
    queryFn: () => downloadMediaBlob(mediaId),
    enabled: enabled && !!mediaId && !localAttachment,
    staleTime: Infinity,
    cacheTime: 5 * 60 * 1000,
    retry: 1,
    onError,
  });

  return {
    blob: (localAttachment?.file as Blob | undefined) ?? query.data,
    /** Object URL owned by the store attachment, if any. Do not revoke. */
    localUrl: localAttachment?.previewUrl,
    isLoading: !localAttachment && !!mediaId && enabled && query.isLoading,
    isError: !localAttachment && query.isError,
    refetch: query.refetch,
  };
};

/** Creates an object URL for `blob` and revokes it on change/unmount. */
export const useObjectUrl = (blob: Blob | undefined) => {
  const [url, setUrl] = useState<string>();

  useEffect(() => {
    if (!blob) {
      setUrl(undefined);
      return;
    }
    const nextUrl = URL.createObjectURL(blob);
    setUrl(nextUrl);
    return () => URL.revokeObjectURL(nextUrl);
  }, [blob]);

  return url;
};

/** Display URL for a signature image: the store attachment's preview, else a downloaded copy. */
export const useMediaImageUrl = (mediaId: string, options?: UseMediaBlobOptions) => {
  const { blob, localUrl, isLoading, isError, refetch } = useMediaBlob(mediaId, options);
  const downloadedUrl = useObjectUrl(localUrl ? undefined : blob);
  return { url: localUrl ?? downloadedUrl, isLoading, isError, refetch };
};
